import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { SCHOOL_NAME, SCHOOL_LOCATION, SCHOOL_MOTTO, SCHOOL_TAGLINE } from "@/lib/branding";
import { AdBanner } from "@/components/app/AdBanner";
import Logo from "@/components/app/Logo";
import { Loader2, LogIn } from "lucide-react";
import { toast } from "sonner";

export default function LoginPage() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  if (user) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      const u = await login(email.trim().toLowerCase(), password);
      toast.success(`Welcome, ${u.name}`);
      navigate("/");
    } catch (e2) {
      setErr(formatApiError(e2));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-white">
      {/* Left crest panel */}
      <section className="hidden lg:flex flex-col justify-between bg-carmel-navy text-white p-16 grain-overlay relative">
        <div className="flex items-center gap-3 text-xs uppercase tracking-[0.3em] text-white/60">
          <div className="w-8 h-px bg-carmel-gold" />
          Est. Carmel Family of Schools
        </div>

        <div className="flex flex-col items-start">
          <Logo
            className="w-64 h-64 p-5 shadow-elevated"
            testId="login-logo-large"
          />
          <h2 className="font-serif text-5xl tracking-tight leading-tight mt-8">
            {SCHOOL_NAME.split(" ").slice(0, 2).join(" ")}
            <br />
            <span className="text-carmel-goldLight">{SCHOOL_NAME.split(" ").slice(2).join(" ")}</span>
          </h2>
          <p className="mt-2 text-sm text-white/70 tracking-wider uppercase">{SCHOOL_LOCATION}</p>
          <p className="mt-8 font-serif italic text-xl text-carmel-goldLight/90 max-w-md">
            "{SCHOOL_MOTTO}"
          </p>
          <p className="mt-2 text-[11px] tracking-[0.35em] uppercase text-white/50">{SCHOOL_TAGLINE}</p>
        </div>

        <p className="text-xs text-white/40">Discipline · Grooming · ID · Punctuality — Daily inspection console</p>
      </section>

      {/* Right form */}
      <section className="flex items-center justify-center px-6 py-12 lg:px-16">
        <div className="w-full max-w-md">
          <div className="flex flex-col items-center text-center lg:hidden mb-8">
            <Logo
              className="w-32 h-32 p-3 shadow-card mb-3"
              testId="login-logo-mobile"
            />
            <p className="font-serif text-xl tracking-tight text-carmel-navy">{SCHOOL_NAME}</p>
            <p className="text-[10px] uppercase tracking-widest text-carmel-navy/50 mt-1">{SCHOOL_LOCATION}</p>
          </div>

          <p className="text-xs uppercase tracking-[0.3em] text-carmel-gold mb-3">Staff Portal</p>
          <h1 className="font-serif text-4xl tracking-tight text-carmel-navy mb-2">Welcome back.</h1>
          <p className="text-carmel-navy/60 mb-10 text-sm">
            Sign in with your teacher credentials to record daily inspections and view class reports.
          </p>

          <form onSubmit={submit} className="space-y-5" data-testid="login-form">
            <div>
              <Label htmlFor="email" className="text-xs uppercase tracking-wider text-carmel-navy/70">Email</Label>
              <Input
                id="email"
                type="email"
                required
                data-testid="login-email-input"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1.5 h-11"
                placeholder="teacher@carmel.edu"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-xs uppercase tracking-wider text-carmel-navy/70">Password</Label>
              <Input
                id="password"
                type="password"
                required
                data-testid="login-password-input"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1.5 h-11"
                placeholder="••••••••"
              />
            </div>

            {err && (
              <p className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-md px-3 py-2" data-testid="login-error">
                {err}
              </p>
            )}

            <Button
              type="submit"
              data-testid="login-submit-button"
              disabled={busy}
              className="w-full h-11 bg-carmel-gold hover:bg-carmel-goldDark text-white transition-colors duration-200"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><LogIn className="w-4 h-4 mr-2" /> Sign in</>}
            </Button>
          </form>

          <div className="mt-10 pt-6 border-t border-carmel-navy/10 text-xs text-carmel-navy/50">
            <p>Only authorised school staff. Your username and password are issued by the administrator — contact them if you need access or a password reset.</p>
          </div>

          <div className="mt-6">
            <AdBanner className="min-h-[90px]" />
          </div>
        </div>
      </section>
    </div>
  );
}
